import { useState } from 'react';
import { Row, Column, Tabs, Spin, Alert } from '@bancobarigui/barihaus';
import type { TabsProps } from 'antd';
import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { useAuth } from '@/contexts/auth.context';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { KPICard } from './components/KPICard';
import { StatusPieChart } from './components/StatusPieChart';
import { InspectionTypePieChart } from './components/InspectionTypePieChart';
import { TopProvidersChart } from './components/TopProvidersChart';
import { TopCitiesChart } from './components/TopCitiesChart';
import { MonthlyEvolutionChart } from './components/MonthlyEvolutionChart';
import { CompletionTrendChart } from './components/CompletionTrendChart';
import { MonthlyStatusBreakdownChart } from './components/MonthlyStatusBreakdownChart';
import { YearComparisonChart } from './components/YearComparisonChart';
import { RejectionRateChart } from './components/RejectionRateChart';
import InspectionMapView from './components/InspectionMapView';
import { ProviderComparisonChart } from './components/ProviderComparisonChart';
import { ReportsFilters } from './components/ReportsFilters';
import { DrillDownModal } from './components/DrillDownModal';
import { DetailInspection } from '@/components/DetailInspection';
import { useReportsData } from './useReportsData';
import { useDrillDown } from './useDrillDown';
import { getReportsAccessConfig, isChartVisible, CHART_IDS } from './reportsPermissions';

export default function Reports() {
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  
  const { user } = useAuth();
  const { isProviderUser } = usePermissions();

  
  const isMasterProvider = isProviderUser();
  const isAdminBank = !isMasterProvider;

  
  
  const showMasterCharts = isAdminBank;
  const showOperationalCharts = true; 

  
  const accessConfig = getReportsAccessConfig(
    user?.roles || [],
    user?.id,
    user?.providerId
  );

  
  if (!accessConfig.canViewReports) {
    return (
      <ProtectedRouteContainer>
        <Alert
          message="Acesso Negado"
          description="Você não tem permissão para visualizar os relatórios."
          type="error"
          showIcon
        />
      </ProtectedRouteContainer>
    );
  }

  
  const {
    
    kpiData,
    isLoadingKPIs,


    statusDistribution,
    inspectionTypeDistribution,
    topProviders,
    topOperationalProviders,
    topCities,


    monthlyEvolution,
    completionTrend,
    monthlyStatusBreakdown,
    yearComparison,


    rejectionRate,
    operationalRejectionRate,
    providerComparison,
    operationalProviderComparison,

    
    inspectionLocations,

    
    isLoadingCharts,

    
    activeFilters,
    getEffectiveFilters,

    
    handleFilterChange,
    handleClearFilters,
  } = useReportsData();

  
  const mappedOperationalProviders = topOperationalProviders.map(op => ({
    providerId: op.operationalUserId,
    name: op.operationalUserName,
    total: op.totalInspections
  }));

  const mappedOperationalComparison = operationalProviderComparison.map(op => ({
    providerId: op.operationalUserId,
    name: op.operationalUserName,
    statusCounts: op.statusCounts,
    totalInspections: op.totalInspections,
    completionRate: op.completionRate,
    averageCompletionTime: op.averageCompletionTime
  }));

  const mappedOperationalRejection = operationalRejectionRate.map(op => ({
    providerId: op.operationalUserId,
    provider: op.operationalUserName,
    rate: op.rejectionRate
  }));


  const {
    isDrillDownModalOpen,
    drillDownTitle,
    drillDownFilters,
    closeDrillDown,
    handleStatusDrillDown,
    handleInspectionTypeDrillDown,
    handleProviderDrillDown,
    handleCityDrillDown,
    handleMonthDrillDown,
  } = useDrillDown({ getEffectiveFilters });

  
  const handleInspectionClick = (inspectionId: string, itemId: string) => {
    setSelectedInspectionId(inspectionId);
    setSelectedItemId(itemId);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedInspectionId(null);
    setSelectedItemId(null);
  };

  const tabItems: TabsProps['items'] = [
    {
      key: '1',
      label: 'Visão Geral',
      children: (
        <div>
          {}
          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Column xs={24} sm={12} lg={6}>
              <KPICard
                title="Total de Vistorias"
                value={kpiData?.totalVistorias ?? 0}
                previousValue={kpiData?.totalVistoriasMesAnterior ?? 0}
                loading={isLoadingKPIs}
              />
            </Column>
            <Column xs={24} sm={12} lg={6}>
              <KPICard
                title="Taxa de Conclusão"
                value={kpiData?.taxaConclusao ?? 0}
                previousValue={kpiData?.taxaConclusaoMesAnterior ?? 0}
                suffix="%"
                precision={1}
                loading={isLoadingKPIs}
              />
            </Column>
            <Column xs={24} sm={12} lg={6}>
              <KPICard
                title="Tempo Médio de Execução"
                value={kpiData?.tempoMedioExecucao ?? 0}
                previousValue={kpiData?.tempoMedioExecucaoMesAnterior ?? 0}
                suffix="dias"
                precision={1}
                loading={isLoadingKPIs}
              />
            </Column>
            <Column xs={24} sm={12} lg={6}>
              <KPICard
                title="Vistorias Pendentes"
                value={kpiData?.vistoriasPendentes ?? 0}
                previousValue={kpiData?.vistoriasPendentesMesAnterior ?? 0}
                loading={isLoadingKPIs}
              />
            </Column>
          </Row>

          {}
          {(isChartVisible(CHART_IDS.STATUS_DISTRIBUTION, 'overview', accessConfig) ||
            isChartVisible(CHART_IDS.INSPECTION_TYPE_DISTRIBUTION, 'overview', accessConfig)) && (
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              {isChartVisible(CHART_IDS.STATUS_DISTRIBUTION, 'overview', accessConfig) && (
                <Column xs={24} lg={12}>
                  <StatusPieChart
                    data={statusDistribution}
                    loading={isLoadingCharts}
                    onSegmentClick={handleStatusDrillDown}
                  />
                </Column>
              )}
              {isChartVisible(CHART_IDS.INSPECTION_TYPE_DISTRIBUTION, 'overview', accessConfig) && (
                <Column xs={24} lg={12}>
                  <InspectionTypePieChart
                    data={inspectionTypeDistribution}
                    loading={isLoadingCharts}
                    onSegmentClick={handleInspectionTypeDrillDown}
                  />
                </Column>
              )}
            </Row>
          )}

          {}
          {(isChartVisible(CHART_IDS.TOP_PROVIDERS, 'overview', accessConfig) ||
            isChartVisible(CHART_IDS.TOP_CITIES, 'overview', accessConfig)) && (
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              {}
              {isChartVisible(CHART_IDS.TOP_PROVIDERS, 'overview', accessConfig) && showMasterCharts && (
                <Column xs={24} lg={12}>
                  <TopProvidersChart
                    data={topProviders}
                    loading={isLoadingCharts}
                    onBarClick={handleProviderDrillDown}
                    title="Top Prestadoras Master (por volume)"
                  />
                </Column>
              )}
              {}
              {isChartVisible(CHART_IDS.TOP_PROVIDERS, 'overview', accessConfig) && (
                <Column xs={24} lg={12}>
                  <TopProvidersChart
                    data={mappedOperationalProviders}
                    loading={isLoadingCharts}
                    onBarClick={undefined}
                    title="Top Operacionais (por volume)"
                  />
                </Column>
              )}
              {isChartVisible(CHART_IDS.TOP_CITIES, 'overview', accessConfig) && (
                <Column xs={24} lg={isChartVisible(CHART_IDS.TOP_PROVIDERS, 'overview', accessConfig) ? 8 : 12}>
                  <TopCitiesChart
                    data={topCities}
                    loading={isLoadingCharts}
                    onBarClick={handleCityDrillDown}
                  />
                </Column>
              )}
            </Row>
          )}

        </div>
      ),
    },
    {
      key: '2',
      label: 'Análise Temporal',
      children: (
        <div>
          {}
          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Column xs={24}>
              <MonthlyEvolutionChart
                data={monthlyEvolution}
                loading={isLoadingCharts}
                onPointClick={handleMonthDrillDown}
              />
            </Column>
          </Row>

          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Column xs={24}>
              <CompletionTrendChart data={completionTrend} loading={isLoadingCharts} />
            </Column>
          </Row>

          {}
          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Column xs={24}>
              <MonthlyStatusBreakdownChart data={monthlyStatusBreakdown as any} loading={isLoadingCharts} />
            </Column>
          </Row>

          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Column xs={24}>
              <YearComparisonChart data={yearComparison as any} loading={isLoadingCharts} />
            </Column>
          </Row>
        </div>
      ),
    },
    {
      key: '3',
      label: 'Performance',
      children: (
        <div>
          {}
          {isChartVisible(CHART_IDS.PROVIDER_COMPARISON, 'performance', accessConfig) && (
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              {}
              {isChartVisible(CHART_IDS.PROVIDER_COMPARISON, 'performance', accessConfig) && showMasterCharts && (
                <Column xs={24} lg={24}>
                  <ProviderComparisonChart
                    data={providerComparison}
                    loading={isLoadingCharts}
                    title="Comparativo entre Prestadoras Master"
                  />
                </Column>
              )}
              {}
              {isChartVisible(CHART_IDS.PROVIDER_COMPARISON, 'performance', accessConfig) && (
                <Column xs={24} lg={24}>
                  <ProviderComparisonChart
                    data={mappedOperationalComparison}
                    loading={isLoadingCharts}
                    title="Comparativo entre Operacionais"
                  />
                </Column>
              )}
            </Row>
          )}

          {}
          {isChartVisible(CHART_IDS.REJECTION_RATE, 'performance', accessConfig) && (
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              {}
              {showMasterCharts && (
                <Column xs={24} lg={12}>
                  <RejectionRateChart
                    data={rejectionRate}
                    loading={isLoadingCharts}
                    title="Taxa de Rejeição - Prestadoras Master"
                  />
                </Column>
              )}
              {}
              <Column xs={24} lg={12}>
                <RejectionRateChart
                  data={mappedOperationalRejection}
                  loading={isLoadingCharts}
                  title="Taxa de Rejeição - Operacionais"
                />
              </Column>
            </Row>
          )}
        </div>
      ),
    },
    {
      key: '4',
      label: 'Geográfico',
      children: (
        <div>
          {}
          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Column xs={24}>
              <InspectionMapView
                data={inspectionLocations}
                loading={isLoadingCharts}
                onInspectionClick={handleInspectionClick}
              />
            </Column>
          </Row>
        </div>
      ),
    },
  ];

  
  const filteredTabItems = tabItems.filter(tab =>
    accessConfig.availableTabs.includes(tab.key)
  );

  return (
    <ProtectedRouteContainer
      pageHeader={<PageHeader title="Métricas" />}
    >
      <FeatureAlert services={['inspection']} />
      <ServiceCheck services={['inspection']} featureName="Metricas">
        <div style={{ minHeight: '100vh' }}>
        {}
        <ReportsFilters
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
          disabled={isLoadingCharts || isLoadingKPIs}
        />

        {}
        <Tabs
          defaultActiveKey={filteredTabItems[0]?.key || '1'}
          items={filteredTabItems}
          size="large"
          style={{
            marginTop: '24px',
          }}
        />

        {}
        <DrillDownModal
          visible={isDrillDownModalOpen}
          title={drillDownTitle}
          filters={drillDownFilters}
          onClose={closeDrillDown}
        />

        {}
        {isDetailModalOpen && selectedInspectionId && selectedItemId && (
          <DetailInspection
            open={isDetailModalOpen}
            inspectionId={selectedInspectionId}
            itemId={selectedItemId}
            onClose={handleCloseDetailModal}
          />
        )}
        </div>
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
